import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Compass, Home, Building, TrendingUp, ArrowDown, ArrowRight, ArrowLeft,
  User, Mail, Phone, MapPin, Check, Ruler, PoundSterling
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ArchitectureLeadForm = () => {
  const { isArabic } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const totalSteps = 3;

  const [formData, setFormData] = useState({
    project_type: '',
    property_address: '',
    postcode: '',
    budget_range: '',
    project_description: '',
    name: '',
    email: '',
    phone: '',
    preferred_contact: 'email'
  });

  const projectTypes = [
    { id: 'extension', label: isArabic ? 'توسعة' : 'Extension', icon: ArrowRight },
    { id: 'loft', label: isArabic ? 'تحويل العلية' : 'Loft Conversion', icon: TrendingUp },
    { id: 'basement', label: isArabic ? 'قبو' : 'Basement', icon: ArrowDown },
    { id: 'new_build', label: isArabic ? 'بناء جديد' : 'New Build', icon: Building },
    { id: 'renovation', label: isArabic ? 'تجديد' : 'Renovation', icon: Home },
    { id: 'planning', label: isArabic ? 'تصريح تخطيط' : 'Planning Permission', icon: Compass },
  ];

  const budgetRanges = [
    { id: 'under_50k', label: isArabic ? 'أقل من £50,000' : 'Under £50,000' },
    { id: '50k_100k', label: '£50,000 - £100,000' },
    { id: '100k_200k', label: '£100,000 - £200,000' },
    { id: '200k_500k', label: '£200,000 - £500,000' },
    { id: 'over_500k', label: isArabic ? 'أكثر من £500,000' : 'Over £500,000' },
    { id: 'not_sure', label: isArabic ? 'غير متأكد' : 'Not sure yet' },
  ];

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/leads/architecture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
    }
    setIsSubmitting(false);
  };

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3].map((step) => (
        <React.Fragment key={step}>
          <div 
            className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
              step < currentStep 
                ? 'bg-green-500 text-white' 
                : step === currentStep 
                  ? 'bg-[#404040] text-white' 
                  : 'bg-gray-200 text-gray-500'
            }`}
          >
            {step < currentStep ? <Check className="w-5 h-5" /> : step}
          </div>
          {step < 3 && (
            <div className={`w-16 h-1 mx-1 ${step < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  if (isSubmitted) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-gray-50 to-gray-100" dir={isArabic ? 'rtl' : 'ltr'}>
        <div className="max-w-2xl mx-auto px-4">
          <Card className="border-none shadow-xl">
            <CardContent className="p-8 text-center">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-12 h-12 text-green-500" />
              </div>
              <h2 className="text-3xl font-bold text-[#404040] mb-4">
                {isArabic ? 'شكراً لك!' : 'Thank You!'}
              </h2>
              <p className="text-lg text-gray-600 mb-2">
                {isArabic 
                  ? 'تم استلام استفسارك المعماري'
                  : 'Your architecture enquiry has been received'
                }
              </p>
              <p className="text-gray-500 mb-8">
                {isArabic 
                  ? 'سيتواصل معك أحد مستشارينا خلال 24 ساعة'
                  : 'One of our consultants will contact you within 24 hours'
                }
              </p>
              <Button 
                onClick={() => window.location.href = '/architecture'}
                className="bg-[#404040] hover:bg-[#333] text-white"
              >
                {isArabic ? 'العودة للخدمات المعمارية' : 'Back to Architecture'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-gray-50 to-gray-100" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#404040] rounded-full mb-4">
            <Compass className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#404040]">
            {isArabic ? 'استفسار عن مشروع معماري' : 'Architecture Project Enquiry'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isArabic 
              ? 'أخبرنا عن مشروعك وسنتواصل معك'
              : 'Tell us about your project and we\'ll get back to you'
            }
          </p>
        </div>

        {renderStepIndicator()}

        <Card className="border-none shadow-xl">
          <CardContent className="p-8">
            {/* Step 1: Project Type */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-bold text-[#404040]">
                    {isArabic ? 'ما نوع مشروعك؟' : 'What type of project?'}
                  </h2>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {projectTypes.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setFormData({...formData, project_type: type.id})}
                      className={`p-4 rounded-xl border-2 transition-all text-center ${
                        formData.project_type === type.id
                          ? 'border-[#404040] bg-[#404040]/5'
                          : 'border-gray-200 hover:border-[#404040]/50'
                      }`}
                    >
                      <type.icon className={`w-8 h-8 mx-auto mb-2 ${formData.project_type === type.id ? 'text-[#404040]' : 'text-gray-400'}`} />
                      <p className="font-medium text-[#404040] text-sm">{type.label}</p>
                    </button>
                  ))}
                </div>

                <div className="mt-6">
                  <Label className="text-lg font-semibold text-[#404040] mb-4 block">
                    {isArabic ? 'نطاق الميزانية' : 'Budget Range'}
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    {budgetRanges.map((range) => (
                      <button
                        key={range.id}
                        type="button"
                        onClick={() => setFormData({...formData, budget_range: range.id})}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          formData.budget_range === range.id
                            ? 'border-[#404040] bg-[#404040]/5'
                            : 'border-gray-200 hover:border-[#404040]/50'
                        }`}
                      >
                        <span className="font-medium text-[#404040] text-sm">{range.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Property Details */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-bold text-[#404040]">
                    {isArabic ? 'تفاصيل العقار' : 'Property Details'}
                  </h2>
                </div>

                <div>
                  <Label htmlFor="property_address">{isArabic ? 'عنوان العقار' : 'Property Address'}</Label>
                  <div className="relative mt-1">
                    <MapPin className={`absolute top-3 ${isArabic ? 'right-3' : 'left-3'} w-5 h-5 text-gray-400`} />
                    <Input
                      id="property_address"
                      placeholder={isArabic ? 'أدخل العنوان' : 'Enter address'}
                      value={formData.property_address}
                      onChange={(e) => setFormData({...formData, property_address: e.target.value})}
                      className={`${isArabic ? 'pr-10' : 'pl-10'} py-6`}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="postcode">{isArabic ? 'الرمز البريدي' : 'Postcode'}</Label>
                  <Input
                    id="postcode"
                    placeholder="e.g., HA3 5AH"
                    value={formData.postcode}
                    onChange={(e) => setFormData({...formData, postcode: e.target.value})}
                    className="mt-1 py-6"
                  />
                </div>

                <div>
                  <Label htmlFor="project_description">{isArabic ? 'وصف المشروع' : 'Project Description'}</Label>
                  <Textarea
                    id="project_description"
                    placeholder={isArabic 
                      ? 'أخبرنا المزيد عن مشروعك...'
                      : 'Tell us more about your project...'
                    }
                    value={formData.project_description}
                    onChange={(e) => setFormData({...formData, project_description: e.target.value})}
                    className="mt-1 min-h-[120px]"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Contact Details */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-bold text-[#404040]">
                    {isArabic ? 'معلومات الاتصال' : 'Contact Details'}
                  </h2>
                </div>

                <div>
                  <Label htmlFor="name">{isArabic ? 'الاسم الكامل' : 'Full Name'}</Label>
                  <div className="relative mt-1">
                    <User className={`absolute top-3 ${isArabic ? 'right-3' : 'left-3'} w-5 h-5 text-gray-400`} />
                    <Input
                      id="name"
                      placeholder={isArabic ? 'أدخل اسمك' : 'Enter your name'}
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className={`${isArabic ? 'pr-10' : 'pl-10'} py-6`}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">{isArabic ? 'البريد الإلكتروني' : 'Email Address'}</Label>
                  <div className="relative mt-1">
                    <Mail className={`absolute top-3 ${isArabic ? 'right-3' : 'left-3'} w-5 h-5 text-gray-400`} />
                    <Input
                      id="email"
                      type="email"
                      placeholder={isArabic ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className={`${isArabic ? 'pr-10' : 'pl-10'} py-6`}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone">{isArabic ? 'رقم الهاتف' : 'Phone Number'}</Label>
                  <div className="relative mt-1">
                    <Phone className={`absolute top-3 ${isArabic ? 'right-3' : 'left-3'} w-5 h-5 text-gray-400`} />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder={isArabic ? 'أدخل رقم هاتفك' : 'Enter your phone number'}
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className={`${isArabic ? 'pr-10' : 'pl-10'} py-6`}
                    />
                  </div>
                </div>

                <div>
                  <Label>{isArabic ? 'طريقة الاتصال المفضلة' : 'Preferred Contact Method'}</Label>
                  <div className="grid grid-cols-3 gap-3 mt-2">
                    {['phone', 'email', 'whatsapp'].map((method) => (
                      <button
                        key={method}
                        type="button"
                        onClick={() => setFormData({...formData, preferred_contact: method})}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          formData.preferred_contact === method
                            ? 'border-[#404040] bg-[#404040]/5'
                            : 'border-gray-200 hover:border-[#404040]/50'
                        }`}
                      >
                        <span className="font-medium text-[#404040] capitalize text-sm">
                          {method === 'phone' ? (isArabic ? 'هاتف' : 'Phone') :
                           method === 'email' ? (isArabic ? 'بريد' : 'Email') : 'WhatsApp'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className={`flex justify-between mt-8 pt-6 border-t ${isArabic ? 'flex-row-reverse' : ''}`}>
              {currentStep > 1 ? (
                <Button variant="outline" onClick={prevStep} className="px-6">
                  <ArrowLeft className={`w-5 h-5 ${isArabic ? 'ml-2 rotate-180' : 'mr-2'}`} />
                  {isArabic ? 'السابق' : 'Previous'}
                </Button>
              ) : (
                <div></div>
              )}

              {currentStep < totalSteps ? (
                <Button 
                  onClick={nextStep} 
                  className="bg-[#404040] hover:bg-[#333] text-white px-6"
                  disabled={currentStep === 1 && !formData.project_type}
                >
                  {isArabic ? 'التالي' : 'Next'}
                  <ArrowRight className={`w-5 h-5 ${isArabic ? 'mr-2 rotate-180' : 'ml-2'}`} />
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit} 
                  className="bg-green-500 hover:bg-green-600 text-white px-8"
                  disabled={isSubmitting || !formData.name || !formData.email || !formData.phone}
                >
                  {isSubmitting ? (isArabic ? 'جاري الإرسال...' : 'Submitting...') : (isArabic ? 'إرسال الاستفسار' : 'Submit Enquiry')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
